import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { PlatformAssistantConfigurationForm } from '@/components/assistantConfiguration/PlatformAssistantConfigurationForm';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PageContainer } from '@/components/layout/PageContainer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { usePlatformAssistantConfigurationQuery } from '@/hooks/useAssistantConfiguration';
import { UsageInsightsSection } from './usage/UsageInsightsSection';

type AssistantAdminTab = 'usage' | 'defaults';

const TAB_SEARCH_PARAMETER = 'tab';
const USAGE_PERMISSION = 'assistant_usage.read';
const DEFAULTS_PERMISSION = 'assistant_configuration.read';

function DefaultsTab() {
  const configurationQuery = usePlatformAssistantConfigurationQuery();
  if (configurationQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }
  if (configurationQuery.isError === true || configurationQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          configurationQuery.refetch();
        }}
      />
    );
  }
  return <PlatformAssistantConfigurationForm saved={configurationQuery.data} />;
}

export function AssistantConfigurationPage() {
  const { t } = useTranslation('assistant');
  const canSeeUsage = useHasPlatformPermission(USAGE_PERMISSION);
  const canSeeDefaults = useHasPlatformPermission(DEFAULTS_PERMISSION);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const requestedTab = searchParameters.get(TAB_SEARCH_PARAMETER);
  const activeTab: AssistantAdminTab =
    canSeeUsage && (requestedTab !== 'defaults' || canSeeDefaults === false) ? 'usage' : 'defaults';

  return (
    <PageContainer width="wide">
      <AdminPageHeader
        title={t('configuration.platformTitle')}
        subtitle={t('insights.pageSubtitle')}
      />
      <Tabs
        value={activeTab}
        idPrefix="assistant-admin"
        onValueChange={function selectTab(nextTab) {
          setSearchParameters({ [TAB_SEARCH_PARAMETER]: nextTab }, { replace: true });
        }}
      >
        <TabsList ariaLabel={t('configuration.platformTitle')} className="mb-6">
          {canSeeUsage ? <TabsTrigger value="usage">{t('insights.tabs.usage')}</TabsTrigger> : null}
          {canSeeDefaults ? (
            <TabsTrigger value="defaults">{t('insights.tabs.defaults')}</TabsTrigger>
          ) : null}
        </TabsList>
        {canSeeUsage ? (
          <TabsContent value="usage">
            <UsageInsightsSection />
          </TabsContent>
        ) : null}
        {canSeeDefaults ? (
          <TabsContent value="defaults">
            <p className="text-xs text-zoomuted mb-5">{t('configuration.platformSubtitle')}</p>
            <DefaultsTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </PageContainer>
  );
}
